import { useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";

const hm = (s) => new Date(s).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" });

const History = ({ items }) => (
  <p data-testid="reschedule-history" className="mt-1 text-xs">
    Reprogramado: {items.map((x) => `${x.bookings} pasaje(s) al ${x.date} ${x.departure} hs (${hm(x.at)}, ${x.by})`).join(" · ")}
  </p>
);

const CancelNotice = ({ trip, onDone }) => {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm("¿Avisar por email a todos los pasajeros que el viaje fue cancelado? Les llega un link para elegir otro horario sin cargo.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/trips/${trip.id}/cancel-notice`, { message: msg });
      toast.success(`Aviso enviado a ${data.emails} email(s) · ${data.bookings} pasaje(s)`);
      setMsg(""); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
      <input data-testid="cancel-notice-message" value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={300} placeholder="Motivo de la cancelación (opcional)" className="h-11 min-w-0 rounded-xl border border-black/10 bg-white px-3 text-[var(--ink)]" />
      <button onClick={send} disabled={busy} data-testid="cancel-notice-send" className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-red-800 px-4 text-sm font-bold text-white">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Avisar cancelación</button>
    </div>
  );
};

export const ReschedulePassengers = ({ trip, canMove, onDone }) => {
  const [opts, setOpts] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/trips/${trip.id}/reschedule-options`);
      setOpts(data);
      setTarget(data?.options?.find((o) => o.missing === 0)?.id ?? "");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const chosen = opts?.options?.find((o) => o.id === target);
  const send = async () => {
    if (!chosen || !window.confirm(`¿Pasar ${opts.bookings} pasaje(s) al ${chosen.date} ${chosen.departure} hs y avisarles por email?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/trips/${trip.id}/reschedule`, { target_trip_id: target });
      toast.success(`${data.bookings} pasaje(s) reprogramados · ${data.emails} email(s) enviados${data.changed_seats ? ` · ${data.changed_seats} butaca(s) cambiadas` : ""}`);
      setOpts(null); onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="reschedule-panel" className="rounded-2xl bg-red-50 p-4 text-red-950 ring-1 ring-red-200">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-bold"><CalendarClock className="h-4 w-4" /> Servicio cancelado · reprogramar pasajeros</p>
        {!opts && canMove && <button onClick={open} disabled={busy} data-testid="reschedule-open" className="inline-flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Pasar a otro horario</button>}
      </div>
      {trip.reschedules?.length > 0 && <History items={trip.reschedules} />}
      {trip.cancel_notices?.length > 0 && <p data-testid="cancel-notice-history" className="mt-1 text-xs">Avisos de cancelación: {trip.cancel_notices.map((x) => `${x.emails} email(s) (${hm(x.at)}, ${x.by})`).join(" · ")}</p>}
      {canMove && !opts && <CancelNotice trip={trip} onDone={onDone} />}
      {opts && (
        <div className="mt-3 space-y-2">
          <p data-testid="reschedule-count" className="text-sm">{opts.bookings} pasaje(s) · {opts.seats} butaca(s) para mover. Se mantiene la butaca si está libre; si no, se asigna otra de la misma categoría.</p>
          {opts.options?.length ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <select data-testid="reschedule-target" value={target} onChange={(e) => setTarget(e.target.value)} className="h-11 w-full min-w-0 rounded-xl border border-black/10 bg-white px-3 text-[var(--ink)]">
                <option value="">Elegí el nuevo horario</option>
                {opts.options.map((o) => (
                  <option key={o.id} value={o.id} disabled={o.missing > 0}>
                    {o.date} · {o.departure} hs · Int. {o.unit ?? "—"} · {o.missing > 0 ? `faltan ${o.missing} lugar(es)` : o.changed_seats ? `entran todos (${o.changed_seats} cambian butaca)` : "entran todos, mismas butacas"}
                  </option>
                ))}
              </select>
              <div className="flex gap-2">
                <button onClick={send} disabled={busy || !chosen} data-testid="reschedule-confirm" className="inline-flex h-11 items-center gap-2 rounded-xl bg-[var(--forest)] px-4 text-sm font-bold text-white disabled:opacity-40">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Mover y avisar</button>
                <button onClick={() => setOpts(null)} data-testid="reschedule-cancel" className="h-11 rounded-xl px-3 text-sm font-semibold">Cerrar</button>
              </div>
            </div>
          ) : <p data-testid="reschedule-empty" className="text-sm font-semibold">No hay otros horarios futuros de esta ruta con unidad asignada.</p>}
        </div>
      )}
    </div>
  );
};
